/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-study-assessments-grid.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { AdminStudyAssessmentsInterface } from '@components/admin/study-assessments-grid/admin-study-assessments-grid.interface';
import ExpandableGridV2 from '../../core/expandable/grid-v2';
import { GridColumnMenuActiveFilter, GridColumnDateFilter } from '../../core/grid-filters';
import { ExtraMenuItem, ExtraMenuItemPosition } from '../../core/grid-header/grid-menu/grid-menu.interface';
import { useGridCells, useTranslation } from '@hooks/index';
import { convertRules } from '@utils/convertor';
import {
    studyCrfAssessments_OpraQueries_crfAssessments_StudyAssessment,
    studyCrfAssessments_OpraQueries_crfAssessments_StudyAssessment_assessmentRules_Rule,
} from '@queries/generated/study-crfs.generated';
import './styles.scss';

const AdminStudyAssessmentsGrid = ({
    assessments,
    canEdit,
    sysAdminCanEditStudy,
    showAddAssessmentsDialog,
    setCurrentAssessment,
}: AdminStudyAssessmentsInterface) => {
    const { t } = useTranslation();
    const { YesNoCell, DateCell, NumberCell, MaxLengthCell } = useGridCells();
    const [assessmentArray, setAssessmentArray] = useState<any>([]);
    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.right,
            tooltipTitle: t('admin:addAssesments'),
            onClick: showAddAssessmentsDialog,
            customClassName: 'grid-header__item--always-visible',
            disabled: !canEdit,
            icon: ['fal', 'plus'],
        },
    ];

    useEffect(() => {
        if (assessments?.length) {
            const array = assessments.map(
                (assessmentItem: studyCrfAssessments_OpraQueries_crfAssessments_StudyAssessment) => ({
                    ...assessmentItem,
                    _readableRules: convertRules(
                        assessmentItem?.assessmentRules as studyCrfAssessments_OpraQueries_crfAssessments_StudyAssessment_assessmentRules_Rule[],
                    ),
                }),
            );

            setAssessmentArray(array);
        }
    }, [assessments]);

    return (
        <ExpandableGridV2
            customKey="admin-study-assessments-grid"
            title={t('admin:manageAssessments')}
            id="admin-study-assessments-grid"
            key="admin-study-assessments-grid"
            items={assessmentArray}
            groupable
            showPager
            scrollable="scrollable"
            reorderable
            resizable
            hideCheckbox
            hideExpand
            pageable={false}
            rowActions={(item: studyCrfAssessments_OpraQueries_crfAssessments_StudyAssessment) => [
                {
                    label: 'admin:editAssessment',
                    icon: ['fal', 'edit'],
                    onClick: () => {
                        setCurrentAssessment(item);
                        showAddAssessmentsDialog();
                    },
                    enabled: canEdit || sysAdminCanEditStudy,
                },
            ]}
            columns={[
                {
                    field: 'name',
                    label: t('admin:name'),
                    minWidth: 140,
                    id: 'assessment-name',
                    lockedByDefault: true,
                    cells: { data: MaxLengthCell(50) },
                },
                {
                    field: '_readableRules',
                    label: t('admin:additionalRules'),
                    minWidth: 150,
                    id: 'assesments-additional-rules',
                    cells: { data: MaxLengthCell(50) },
                },
                {
                    field: 'endPoint',
                    label: t('admin:endPoint'),
                    minWidth: 160,
                    id: 'assessment-end-point',
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    field: 'safety',
                    label: t('admin:safety'),
                    minWidth: 140,
                    id: 'assessment-safety',
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    field: 'lab',
                    label: t('admin:lab'),
                    minWidth: 140,
                    id: 'assessment-lab',
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    field: 'sendToVendor',
                    label: t('admin:sendToVendor'),
                    minWidth: 190,
                    id: 'assessment-send-vendor',
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    field: 'active',
                    label: t('admin:active'),
                    minWidth: 140,
                    id: 'assessment-active',
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    field: '_crfName',
                    label: t('admin:crfName'),
                    minWidth: 140,
                    id: 'assessment-crf-name',
                    cells: { data: MaxLengthCell(30) },
                },
                {
                    label: t('ra:createdBy'),
                    id: 'assessment-createdby',
                    field: '_createdByString',
                    minWidth: 180,
                },
                {
                    label: t('ra:createdDate'),
                    id: 'assessment-created',
                    minWidth: 200,
                    field: 'created',
                    cells: { data: DateCell },
                    filterUi: GridColumnDateFilter,
                },
                {
                    label: t('ra:updatedBy'),
                    id: 'assessment-updatedby',
                    field: '_updatedByString',
                    minWidth: 180,
                },
                {
                    label: t('ra:updatedDate'),
                    field: 'updated',
                    id: 'assessment-updated',
                    minWidth: 200,
                    filterUi: GridColumnDateFilter,
                    cells: { data: DateCell },
                },
                {
                    field: 'recordId',
                    label: t('id'),
                    minWidth: 120,
                    id: 'assessment-id',
                    filter: 'numeric',
                    cells: { data: NumberCell },
                    showByDefault: false,
                },
            ]}
            extraMenuItems={extraMenuItems}
        />
    );
};

export default AdminStudyAssessmentsGrid;
